import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig
} from 'axios'

import { removeToken, setToken } from '@/lib/utils/auth'
import { writeLocaleCookie } from '@/lib/utils/locale-cookie'

import { getDeviceId, resolveDeviceId } from '@/lib/utils/device-id'

import { getBaseURL, getCurrentLanguage, getCustomHeaders, getDigenToken } from './utils'

// API 基础配置接口
interface HttpConfig {
  baseURL?: string
  timeout?: number
  headers?: Record<string, string>
  isFreeMode?: boolean
}

// HTTP客户端类
export class HttpClient {
  private instance: AxiosInstance
  private defaultConfig: HttpConfig = {
    isFreeMode: true
  }
  constructor(config?: HttpConfig) {
    // 默认配置
    this.defaultConfig = {
      baseURL: getBaseURL(),
      timeout: 60 * 1000,
      headers: {
        'Content-Type': 'application/json',
        ...getCustomHeaders()
      },
      isFreeMode: true,  // 保留默认值
      ...config
    }

    // 创建axios实例
    this.instance = axios.create(this.defaultConfig)

    // 初始化拦截器
    this.initializeRequestInterceptor()
    this.initializeResponseInterceptor()
  }

  /**
   * 初始化请求拦截器
   */
  private initializeRequestInterceptor(): void {
    this.instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // 保留单次请求显式传入的 baseURL（如 explore pub_list 固定打生产）
        const defaultsBaseURL = this.instance.defaults.baseURL
        if (!config.baseURL || config.baseURL === defaultsBaseURL) {
          config.baseURL = getBaseURL()
        }

        // 动态设置 Digen-Token
        const digenToken = getDigenToken()
        if (digenToken) {
          config.headers['Digen-Token'] = digenToken
        }

        // 动态设置 Digen-Language
        config.headers['Digen-Language'] = getCurrentLanguage()

        let deviceId = getDeviceId()
        if (!deviceId) {
          try {
            deviceId = await resolveDeviceId()
          } catch {
            deviceId = null
          }
        }

        if (deviceId) {
          config.headers['Digen-DeviceID'] = deviceId
        } else {
          delete config.headers['Digen-DeviceID']
        }

        return config
      },
      (error: AxiosError) => {
        console.error('❌ Request Error:', error)
        return Promise.reject(error)
      }
    )
  }

  /**
   * 初始化响应拦截器
   */
  private initializeResponseInterceptor(): void {
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ResponseBase<any>>) => {
        // 检查是否是标准响应格式 { errCode: number, data: any }
        const hasErrCode = response.data && typeof response.data === 'object' && 'errCode' in response.data
        
        if (hasErrCode && response.data.errCode !== 0 && this.defaultConfig.isFreeMode) {
          return Promise.reject(response.data)
        }
        
        // 标准格式返回 data.data，非标准格式（如直接返回数组）返回 data
        if (hasErrCode && this.defaultConfig.isFreeMode) {
          return response.data.data
        }
        return response.data
      },
      async (error: AxiosError) => {
        const { response } = error
        console.error('error response:', response)
        return Promise.reject(error)
      }
    )
  }

  /**
   * 设置Digen-Token到cookie
   */
  public setDigenToken(token: string): void {
    setToken(token)
  }

  /**
   * 清除Digen-Token
   */
  public clearDigenToken(): void {
    removeToken()
  }

  /**
   * 设置用户语言到 Cookie（父域 .digen.ai，SameSite=Lax，可跨子域共享）
   *
   * 注意：仅在「用户主动切换语言」和「登录返回 language」时调用，
   * 不要在 URL locale 变化时自动调用，以保证分享链接不污染用户偏好。
   */
  public setUserLanguage(language: string): void {
    writeLocaleCookie(language)
  }

  /**
   * 检查是否已认证
   */
  public isAuthenticated(): boolean {
    return !!getDigenToken()
  }

  /**
   * 公共请求方法
   */
  public request<T = any>(config: InternalAxiosRequestConfig): Promise<T> {
    return this.instance.request<ResponseBase<T>>(config) as Promise<T>
  }

  /**
   * GET 请求
   */
  public get<T = any>(url: string, config?: Partial<InternalAxiosRequestConfig>): Promise<T> {
    return this.instance.get<ResponseBase<T>>(url, config) as Promise<T>
  }

  /**
   * POST 请求
   */
  public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.post<ResponseBase<T>>(url, data, config) as Promise<T>
  }

  /**
   * PUT 请求
   */
  public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.put<ResponseBase<T>>(url, data, config) as Promise<T>
  }

  /**
   * PATCH 请求
   */
  public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.patch<ResponseBase<T>>(url, data, config) as Promise<T>
  }

  /**
   * DELETE 请求
   */
  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.delete<ResponseBase<T>>(url, config) as Promise<T>
  }

  /**
   * 上传文件
   */
  public upload<T = any>(
    url: string,
    formData: FormData,
    onUploadProgress?: (progressEvent: any) => void,
    config?: AxiosRequestConfig
  ): Promise<T> {
    return this.instance.post<ResponseBase<T>>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers
      },
      onUploadProgress
    }) as Promise<T>
  }

  /**
   * 下载文件
   */
  public async download(
    url: string,
    filename?: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    try {
      const response = await this.instance.get(url, {
        ...config,
        responseType: 'blob'
      })

      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('download file error:', error)
      throw error
    }
  }

  /**
   * 更新基础URL
   */
  public setBaseURL(baseURL: string): void {
    this.instance.defaults.baseURL = baseURL
  }

  /**
   * 更新超时时间
   */
  public setTimeout(timeout: number): void {
    this.instance.defaults.timeout = timeout
  }

  /**
   * 获取axios实例（用于特殊需求）
   */
  public getInstance(): AxiosInstance {
    return this.instance
  }

  /**
   * 创建新的HTTP客户端实例
   */
  public static create(config?: HttpConfig): HttpClient {
    return new HttpClient(config)
  }
}

// 创建默认的HTTP客户端实例
export const httpClient = new HttpClient()
export const freeHttpClient = new HttpClient({ isFreeMode: false })
// 为了向后兼容，保持原有的导出方式
export const http = httpClient.getInstance()
export const freeHttp = freeHttpClient.getInstance()
// 导出类型
export type {
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  HttpConfig
}
